import { useQuery, UseQueryOptions } from 'react-query';
import {
  getSingularityJobResultById,
  GetSingularityJobResultByIdResponse,
  SchemaSingularityJobQueuedResponse,
} from 'wg-api-sdk';
import { wgApiSdk } from '../../../app/src/features/api/wgApi';

/**
 * Generic type for job result response with custom result type
 */
export interface SingularityJobResultResponse<TResult = unknown>
  extends Omit<GetSingularityJobResultByIdResponse, 'result'> {
  result?: TResult;
}

interface UseJobBasedFetchConfig<TResult, TEnqueueParams> {
  enqueueFn: (
    params: TEnqueueParams,
  ) => Promise<SchemaSingularityJobQueuedResponse>;
  enqueueQueryKey: (params: TEnqueueParams) => unknown[];
  resultQueryKey: (jobId?: string) => unknown[];
  enqueueParams?: TEnqueueParams;
  jobId?: string;
  /** React Query options */
  queryOptions?: UseQueryOptions<
    SingularityJobResultResponse<TResult>,
    unknown,
    SingularityJobResultResponse<TResult>
  >;
  pollInterval?: number;
}

/**
 * Generic hook for (singularity) job-based fetching pattern:
 * 1. Enqueue a job (optional if jobId is provided)
 * 2. Poll for job results until SUCCEEDED or FAILED
 */
export function useSingularityJobBasedFetch<TResult, TEnqueueParams>({
  enqueueFn,
  enqueueQueryKey,
  resultQueryKey,
  enqueueParams,
  jobId: providedJobId,
  queryOptions,
  pollInterval = 5000,
}: UseJobBasedFetchConfig<TResult, TEnqueueParams>) {
  // First query: Enqueue the job (only if jobId is not provided)
  const enqueueQuery = useQuery({
    enabled: !!enqueueParams && !providedJobId,
    queryKey: enqueueParams ? enqueueQueryKey(enqueueParams) : [],
    queryFn: () => enqueueFn(enqueueParams!),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
  });

  const jobId = providedJobId || enqueueQuery.data?.id;

  // Second query: Poll for job result (depends on jobId from first query or provided jobId)
  // This query will not execute until jobId exists (enabled: !!jobId)
  const resultQuery = useQuery({
    enabled: !!jobId,
    queryKey: resultQueryKey(jobId),
    queryFn: () =>
      getSingularityJobResultById(
        { path: { job_id: jobId! } },
        wgApiSdk,
      ) as Promise<SingularityJobResultResponse<TResult>>,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    retry: false,
    refetchInterval: (data) => {
      if (data?.status === 'SUCCEEDED' || data?.status === 'FAILED') {
        return false;
      }
      // Poll at specified interval while job is in progress
      return pollInterval;
    },
    onSuccess: (data) => {
      // Not calling onSuccess if job is still in progress
      if (data?.status === 'SUCCEEDED' || data?.status === 'FAILED') {
        queryOptions?.onSuccess?.(data);
      }
    },
    onError: queryOptions?.onError,
    onSettled: queryOptions?.onSettled,
  });

  return {
    ...resultQuery,
    // Overwrite isLoading to make the hook behave more like synchronous code
    isLoading:
      resultQuery.data?.status !== 'SUCCEEDED' &&
      resultQuery.data?.status !== 'FAILED',
    isError: (!providedJobId && enqueueQuery.isError) || resultQuery.isError,
    error: (!providedJobId && enqueueQuery.error) || resultQuery.error,
  };
}
